/**
 * DARKNESS mode — full-screen fog; only a shrinking vision circle around the cursor reveals gameplay.
 * Collect glowing points to restore vision. Vision reaching zero ends the run.
 */

import { gameRandom } from "./game-rng";

export type DarknessPoint = { id: number; x: number; y: number };

const POINT_COUNT_DEFAULT = 3;
const VISION_START_FRAC_DEFAULT = 0.27;
const VISION_MAX_FRAC_DEFAULT = 0.4;
/** Fixed vision radius added on pickup (same every time; scales with viewport via maxVisionRadius). */
const VISION_POINT_BOOST_FRAC_OF_MAX_DEFAULT =
    (VISION_START_FRAC_DEFAULT / VISION_MAX_FRAC_DEFAULT) * 0.085;
/** Vision radius lost per second at score 0 (reference px, scaled by layout). */
const VISION_SHRINK_BASE_PX_S_DEFAULT = 10.2;
/** Vision radius lost per second at score 4000+ (reference px, scaled by layout). */
const VISION_SHRINK_MAX_PX_S_DEFAULT = 22.5;
const VISION_SHRINK_SCORE_CAP_DEFAULT = 4000;
/** Vision below this fraction of max starts danger zoom and tension audio. */
const DANGER_VISION_FRAC = 0.35;
const ZOOM_MAX = 1.38;
const ZOOM_RISE_RATE = 1.4;
const ZOOM_FALL_RATE = 5.5;

let pointCount = POINT_COUNT_DEFAULT;
let visionStartFrac = VISION_START_FRAC_DEFAULT;
let visionMaxFrac = VISION_MAX_FRAC_DEFAULT;
let visionShrinkBasePxS = VISION_SHRINK_BASE_PX_S_DEFAULT;
let visionShrinkMaxPxS = VISION_SHRINK_MAX_PX_S_DEFAULT;
let visionShrinkScoreCap = VISION_SHRINK_SCORE_CAP_DEFAULT;

export function setDarknessTuning(opts?: {
    pointCount?: number;
    visionStartFrac?: number;
    visionMaxFrac?: number;
    visionShrinkBasePxS?: number;
    visionShrinkMaxPxS?: number;
    visionShrinkScoreCap?: number;
}): void {
    pointCount =
        opts?.pointCount != null && Number.isFinite(opts.pointCount)
            ? Math.max(0, Math.floor(opts.pointCount))
            : POINT_COUNT_DEFAULT;
    visionStartFrac =
        opts?.visionStartFrac != null && Number.isFinite(opts.visionStartFrac)
            ? Math.max(0.01, Math.min(1, opts.visionStartFrac))
            : VISION_START_FRAC_DEFAULT;
    visionMaxFrac =
        opts?.visionMaxFrac != null && Number.isFinite(opts.visionMaxFrac)
            ? Math.max(visionStartFrac, Math.min(1, opts.visionMaxFrac))
            : Math.max(visionStartFrac, VISION_MAX_FRAC_DEFAULT);
    visionShrinkBasePxS =
        opts?.visionShrinkBasePxS != null && Number.isFinite(opts.visionShrinkBasePxS)
            ? Math.max(0, opts.visionShrinkBasePxS)
            : VISION_SHRINK_BASE_PX_S_DEFAULT;
    visionShrinkMaxPxS =
        opts?.visionShrinkMaxPxS != null && Number.isFinite(opts.visionShrinkMaxPxS)
            ? Math.max(0, opts.visionShrinkMaxPxS)
            : VISION_SHRINK_MAX_PX_S_DEFAULT;
    visionShrinkScoreCap =
        opts?.visionShrinkScoreCap != null && Number.isFinite(opts.visionShrinkScoreCap)
            ? Math.max(1, opts.visionShrinkScoreCap)
            : VISION_SHRINK_SCORE_CAP_DEFAULT;
}

let maxVisionRadius = 280;
let visionRadius = 280;
let darknessZoom = 1;
let darknessDanger = 0;
let points: DarknessPoint[] = [];
let nextPointId = 0;

function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
}

function minDim(canvasW: number, canvasH: number) {
    return Math.min(canvasW, canvasH);
}

function randomPointPos(
    canvasW: number,
    canvasH: number,
    inset: number,
    avoidX: number,
    avoidY: number,
    minDistFromCursor: number,
    existing: DarknessPoint[],
) {
    const pad = inset + 28;
    const xMin = pad;
    const xMax = canvasW - pad;
    const yMin = pad;
    const yMax = canvasH - pad;
    if (xMax <= xMin || yMax <= yMin) {
        return { x: canvasW * 0.5, y: canvasH * 0.5 };
    }
    // Fixed RNG consumption so cursor/existing-point rejection cannot desync replays.
    let found: { x: number; y: number } | null = null;
    for (let attempt = 0; attempt < 48; attempt++) {
        const x = xMin + gameRandom() * (xMax - xMin);
        const y = yMin + gameRandom() * (yMax - yMin);
        if (found) continue;
        if (Math.hypot(x - avoidX, y - avoidY) < minDistFromCursor) continue;
        let ok = true;
        for (const p of existing) {
            if (Math.hypot(x - p.x, y - p.y) < 72) {
                ok = false;
                break;
            }
        }
        if (ok) found = { x, y };
    }
    const fallback = {
        x: xMin + gameRandom() * (xMax - xMin),
        y: yMin + gameRandom() * (yMax - yMin),
    };
    return found || fallback;
}

function spawnPoint(
    canvasW: number,
    canvasH: number,
    inset: number,
    cursorX: number,
    cursorY: number,
    layoutScale: number,
) {
    const pos = randomPointPos(
        canvasW,
        canvasH,
        inset,
        cursorX,
        cursorY,
        Math.max(visionRadius * 0.55, 80 * layoutScale),
        points,
    );
    points.push({ id: nextPointId++, x: pos.x, y: pos.y });
}

function fillPoints(
    canvasW: number,
    canvasH: number,
    inset: number,
    cursorX: number,
    cursorY: number,
    layoutScale: number,
) {
    while (points.length < pointCount) {
        spawnPoint(canvasW, canvasH, inset, cursorX, cursorY, layoutScale);
    }
}

export function resetDarknessState(
    canvasW: number,
    canvasH: number,
    layoutScale: number,
    cursorX: number,
    cursorY: number,
    edgeInsetPx: number,
) {
    const md = minDim(canvasW, canvasH);
    // Fraction of shorter edge only — multiplying by layoutScale double-scaled
    // high-DPI / small viewports and made the lit area cover too much of the screen.
    maxVisionRadius = md * visionMaxFrac;
    visionRadius = md * visionStartFrac;
    visionRadius = clamp(visionRadius, 0, maxVisionRadius);
    darknessZoom = 1;
    darknessDanger = 0;
    points = [];
    nextPointId = 0;
    fillPoints(canvasW, canvasH, edgeInsetPx, cursorX, cursorY, layoutScale);
}

export function getDarknessVisionRadius() {
    return visionRadius;
}

export function getDarknessZoom() {
    return darknessZoom;
}

/** 0 = safe, 1 = maximum tension (vision very small). */
export function getDarknessDanger() {
    return darknessDanger;
}

function shrinkSpeedPxS(score: number, layoutScale: number) {
    const t = clamp(score / visionShrinkScoreCap, 0, 1);
    const pxS = visionShrinkBasePxS + (visionShrinkMaxPxS - visionShrinkBasePxS) * t;
    return pxS * layoutScale;
}

function pointVisionBoost() {
    const boostFrac = (visionStartFrac / Math.max(0.01, visionMaxFrac)) * 0.085;
    return maxVisionRadius * boostFrac;
}

export function updateDarkness(
    dt: number,
    score: number,
    cursorX: number,
    cursorY: number,
    cursorR: number,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
    edgeInsetPx: number,
): { visionDead: boolean; pointCollected: boolean } {
    const shrink = shrinkSpeedPxS(score, layoutScale) * dt;
    visionRadius = Math.max(0, visionRadius - shrink);

    const dangerThreshold = maxVisionRadius * DANGER_VISION_FRAC;
    const rawDanger =
        dangerThreshold > 1 ? clamp(1 - visionRadius / dangerThreshold, 0, 1) : visionRadius <= 0 ? 1 : 0;
    const targetDanger = rawDanger > 0 ? Math.pow(rawDanger, 0.82) : 0;
    const dangerRise = 2.2 * dt;
    const dangerFall = 7 * dt;
    if (targetDanger > darknessDanger) {
        darknessDanger = Math.min(targetDanger, darknessDanger + (targetDanger - darknessDanger) * dangerRise);
    } else {
        darknessDanger = Math.max(targetDanger, darknessDanger - (darknessDanger - targetDanger) * dangerFall);
    }

    const targetZoom = 1 + (ZOOM_MAX - 1) * darknessDanger;
    if (targetZoom > darknessZoom) {
        darknessZoom = Math.min(targetZoom, darknessZoom + (targetZoom - darknessZoom) * ZOOM_RISE_RATE * dt);
    } else {
        darknessZoom = Math.max(targetZoom, darknessZoom - (darknessZoom - targetZoom) * ZOOM_FALL_RATE * dt);
    }

    let pointCollected = false;
    const pickupR = Math.max(14 * layoutScale, cursorR * 1.35);
    for (let i = points.length - 1; i >= 0; i--) {
        const p = points[i];
        if (Math.hypot(p.x - cursorX, p.y - cursorY) <= pickupR + 10 * layoutScale) {
            points.splice(i, 1);
            visionRadius = clamp(visionRadius + pointVisionBoost(), 0, maxVisionRadius);
            pointCollected = true;
            spawnPoint(canvasW, canvasH, edgeInsetPx, cursorX, cursorY, layoutScale);
        }
    }

    fillPoints(canvasW, canvasH, edgeInsetPx, cursorX, cursorY, layoutScale);

    return { visionDead: visionRadius <= 0.5, pointCollected };
}

export function drawDarknessPoints(ctx: CanvasRenderingContext2D, nowMs: number, layoutScale: number) {
    const t = nowMs / 1000;
    const ls = layoutScale;
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const bob = Math.sin(t * 3.2 + i * 1.7) * 4 * ls;
        const pulse = 0.88 + Math.sin(t * 5 + i) * 0.12;
        const rDot = 9 * ls * pulse;
        ctx.save();
        ctx.fillStyle = "#fff6a8";
        ctx.shadowColor = "rgba(255,220,80,0.95)";
        ctx.shadowBlur = 22 * ls;
        ctx.beginPath();
        ctx.arc(p.x, p.y + bob, rDot, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 10 * ls;
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.beginPath();
        ctx.arc(p.x, p.y + bob, rDot * 0.42, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
}

function visionFeatherPx(layoutScale: number) {
    const r = Math.max(0, visionRadius);
    return Math.max(r * 0.38, 24 * layoutScale);
}

/** Clip so gameplay (balls, points, trail, cursor) is drawn only inside the lit circle. */
export function applyDarknessVisionClip(
    ctx: CanvasRenderingContext2D,
    cursorX: number,
    cursorY: number,
    layoutScale: number,
) {
    const r = Math.max(0, visionRadius);
    const feather = visionFeatherPx(layoutScale);
    ctx.beginPath();
    ctx.arc(cursorX, cursorY, r + feather, 0, Math.PI * 2);
    ctx.clip();
}

/** Opaque black outside the vision disc; call before applying the clip. */
export function fillDarknessBackground(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
) {
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvasW, canvasH);
}

/** Clear inside the vision clip so the CSS game atmosphere shows through. */
export function fillDarknessVisionInterior(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
    _backgroundColor?: string,
) {
    ctx.clearRect(0, 0, canvasW, canvasH);
}

/** Soft inner-edge shading (inside the clip, after gameplay is drawn). */
export function drawDarknessEdgeShade(
    ctx: CanvasRenderingContext2D,
    cursorX: number,
    cursorY: number,
    layoutScale: number,
) {
    const r = Math.max(0, visionRadius);
    const feather = visionFeatherPx(layoutScale);
    const rim = ctx.createRadialGradient(
        cursorX,
        cursorY,
        Math.max(0, r - feather * 0.45),
        cursorX,
        cursorY,
        r + feather,
    );
    rim.addColorStop(0, "rgba(0,0,0,0)");
    rim.addColorStop(0.62, "rgba(0,0,0,0.32)");
    rim.addColorStop(1, "rgba(0,0,0,0.9)");
    ctx.fillStyle = rim;
    ctx.beginPath();
    ctx.arc(cursorX, cursorY, r + feather, 0, Math.PI * 2);
    ctx.fill();

    const glow = ctx.createRadialGradient(cursorX, cursorY, Math.max(0, r - 4), cursorX, cursorY, r + feather * 0.45);
    glow.addColorStop(0, "rgba(120,160,255,0)");
    glow.addColorStop(0.75, "rgba(90,130,220,0.05)");
    glow.addColorStop(1, "rgba(60,100,200,0.12)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cursorX, cursorY, r + feather * 0.45, 0, Math.PI * 2);
    ctx.fill();
}
